import React, { FC, useState } from "react";

import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors";
import { Box, Link, Stack, Typography } from "@mui/material";
import StackContent from "../StackContent";

/**
 * Data collection Opt out box
 *
 * We use these services to better understand our customers, and they
 * need a way to opt out of such identification services
 */

const slides = [
  {
    label: "SaaS",
    description:
      "A fully hosted solution, managed entirely by Pangea, and is ideal for teams looking for a quick setup with no infrastructure management. Pangea takes care of everything, enabling you to deploy quickly and focus solely on using the platform.",
    url: "/docs/deployment-models/saas",
  },
  {
    label: "Edge",
    description:
      "Edge combines Pangea's hosted control plane with customer-hosted data processing. Ideal for teams with data residency or latency-sensitive requirements. Data processing occurs within your cloud environment (AWS, Azure, or GCP), while configurations are managed through Pangea's hosted console.",
    url: "/docs/deployment-models/edge",
  },
  {
    label: "Private Cloud",
    description:
      "A fully self-managed deployment where both data and control planes run within your infrastructure. This option offers the highest level of control, security, and performance, supporting AWS, Azure, and GCP.",
    url: "/docs/deployment-models/private-cloud",
  },
];

import { ArrowForwardRounded } from "@mui/icons-material";
import { uiContainerTransitionMs, uiOpacityHover } from "@src/utils";
import ContentGroup from "../ContentGroup";

export const navArrowHoverStyles = {
  ".nav-arrow-forward": {
    opacity: 1,
    transform: `translate3d(0px, 0px, 0px)`,
  },
};

export const Arrow: FC = () => {
  return (
    <Box
      sx={{
        overflow: "visible",
        width: "1px",
        display: "inline-block",
      }}
    >
      <ArrowForwardRounded
        className="nav-arrow-forward"
        sx={{
          width: "12px",
          height: "12px",
          marginLeft: "4px",
          color: Colors.PrimaryMain,
          stroke: Colors.PrimaryMain,
          strokeWidth: "0.5px",
          opacity: 0,
          transform: `translate3d(-8px, 0px, 0px)`,
          transition: `transform ${uiContainerTransitionMs}ms ease-in-out, opacity ${uiContainerTransitionMs}ms ease-in-out !important`,
        }}
      />
    </Box>
  );
};

const DeploymentModels: FC = () => {
  const [slide, setSlide] = useState(0);

  return (
    <Box
      sx={{
        marginBottom: "48px",
      }}
    >
      <ContentGroup size="full">
        <StackContent
          stackMargin={true}
          sx={{
            padding: "64px",
            backgroundColor: Colors.BlueGrey,
            borderRadius: "15px",
            gap: "48px",
            flexDirection: {
              xs: "column-reverse",
              md: "row",
            },
          }}
        >
          <Stack
            sx={{
              justifyContent: "center",
              gap: "24px",
              ".slide": {
                display: "none",
              },
              [`.slide-${slide}`]: {
                display: "block",
              },
              img: {
                height: {
                  sm: "auto",
                  md: "500px",
                  lg: "617px",
                },
                width: {
                  sm: "100%",
                  md: "auto",
                },
              },
            }}
          >
            <Box
              component="img"
              src="/deployment-models/saas.png"
              className="slide slide-0"
            />
            <Box
              component="img"
              src="/deployment-models/edge.png"
              className="slide slide-1"
            />
            <Box
              component="img"
              src="/deployment-models/privatecloud.png"
              className="slide slide-2"
            />
          </Stack>

          <Stack
            sx={{
              gap: "24px",
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: "48px",
                lineHeight: "50px",
                fontWeight: 600,
              }}
            >
              Flexible deployment architectures
            </Typography>

            {[0, 1, 2].map((_slide) => {
              return (
                <Stack
                  onClick={() => {
                    setSlide(_slide);
                  }}
                  role="button"
                  sx={{
                    borderBottom: `solid 1px ${Colors.TextSecondary}`,
                    gap: "16px",
                    paddingBottom: "24px",
                  }}
                >
                  <Stack
                    sx={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      width: "calc(100% - 16px)",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        width: "100%",
                        color: Colors.PrimaryMain,
                        "&:hover": {
                          color: Colors.PrimaryMain,
                          cursor: "pointer",
                        },
                      }}
                    >
                      {slides[_slide].label}
                    </Typography>
                  </Stack>

                  <Stack
                    sx={{
                      display: slide === _slide ? "block" : "none",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: Colors.TextSecondary,
                        marginBottom: "16px",
                      }}
                    >
                      {slides[_slide].description}
                    </Typography>

                    <Link
                      href={slides[_slide].url}
                      sx={{
                        color: Colors.PrimaryMain,
                        display: "block",
                        textDecoration: "none",
                        fontWeight: 400,
                        fontSize: "14px",
                        width: "100%",
                        "&:hover": {
                          color: Colors.PrimaryMain,
                          textDecoration: "none",
                          cursor: "pointer",
                          ...navArrowHoverStyles,
                        },
                        "&:visited": {
                          color: Colors.PrimaryMain,
                        },
                      }}
                      target="_blank"
                    >
                      Learn more
                      <Arrow />
                    </Link>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </StackContent>
      </ContentGroup>
    </Box>
  );
};

export default DeploymentModels;
